import { useEffect, useState } from "react";

import { SCHEDULE_TYPES } from "components/ScheduleTask";
import { API_IS_BEING_CANCELLED, UNKNOWN } from "shared/constants/strings";
import { useApi } from "shared/hooks/useApi";
import { useCompanyLevelSettings } from "shared/hooks/useCompanyLevelSettings";
import { useIsMounted } from "shared/hooks/useIsMounted";
import { ENDPOINTS, FEATURE_SETTINGS } from "shared/types/enums";

import { useTaskTypes } from "../../hooks/useTaskTypes";
import { Task } from "../../types/interfaces";
import { SecondSelectData } from "../SecondSelectContent/types";
import {
    SCHEDULE_TASK_TYPE,
    ScheduledDatePayload,
    ScheduledTimePayload,
    SelectedTask,
    TaskPlatforms
} from "../types";

const useCreateTaskContent = (setPayload: (data: any) => void) => {
    const { cancelSource, getData, handleResponse, isCanceled } = useApi();
    const { isFeatureSettingsRestricted } = useCompanyLevelSettings();
    const { visibleTasks } = useTaskTypes();
    const isMounted = useIsMounted();

    const [arePlatformsLoading, setArePlatformsLoading] = useState(true);
    const [tasks, setTasks] = useState<Task[]>([]);
    const [taskTypeInputValue, setTaskTypeInputValue] = useState("");
    const [selectedTask, setSelectedTask] = useState<SelectedTask>(null);

    const [secondSelectData, setSecondSelectData] =
        useState<SecondSelectData | null>(null);

    const [scheduleType, setScheduleType] = useState<SCHEDULE_TASK_TYPE | null>(
        null
    );

    const [isCancelTasksChecked, setIsCancelTasksChecked] = useState(true);

    const [scheduledDate, setScheduledDate] =
        useState<ScheduledDatePayload | null>(null);

    const [scheduledTime, setScheduledTime] =
        useState<ScheduledTimePayload | null>(null);

    useEffect(() => {
        const getSupportedPlatforms = async () => {
            try {
                const {
                    data: { platform_support_table }
                } = await getData(ENDPOINTS.TaskPlatforms);

                if (isMounted) {
                    const addPlatforms = (platforms: TaskPlatforms) =>
                        visibleTasks.map(task => {
                            const actualPlatforms = platforms[task.type];

                            if (actualPlatforms) {
                                const filteredActualPlatforms =
                                    actualPlatforms.filter(
                                        platform => platform !== UNKNOWN
                                    );

                                return {
                                    ...task,
                                    platforms: filteredActualPlatforms
                                };
                            }

                            return task;
                        });

                    const tasksWithPlatforms = addPlatforms(
                        platform_support_table
                    );

                    const supportedTasks = tasksWithPlatforms.filter(
                        ({ platforms }) => platforms !== undefined
                    );

                    setTasks(supportedTasks);
                }
            } catch (error) {
                if (isMounted && !isCanceled(error)) {
                    setTasks([]);
                    handleResponse(error);
                }
            }

            setArePlatformsLoading(false);
        };

        getSupportedPlatforms();

        return () => {
            cancelSource.cancel(API_IS_BEING_CANCELLED);
        };

        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (
            selectedTask &&
            (selectedTask.secondSelect ? secondSelectData !== null : true)
        ) {
            const getSchedule = () => {
                if (scheduleType === SCHEDULE_TASK_TYPE.ByDate) {
                    return {
                        type: SCHEDULE_TYPES.SpecificDays,
                        attributes: {
                            from_date: scheduledDate?.from ?? null,
                            to_date: scheduledDate?.to ?? null
                        }
                    };
                }

                if (
                    scheduleType === SCHEDULE_TASK_TYPE.ByTime &&
                    scheduledTime
                ) {
                    const { from, duration } = scheduledTime;

                    return {
                        type: SCHEDULE_TYPES.SpecificHours,
                        attributes: {
                            from_minutes: from,
                            duration_minutes: duration
                        }
                    };
                }

                return null;
            };

            const data = {
                type: selectedTask.type,
                secondSelectData,
                schedule: getSchedule(),
                expire_existing_tasks: isCancelTasksChecked
            };

            setPayload(data);
        } else {
            setPayload(null);
        }
        // eslint-disable-next-line
    }, [
        selectedTask,
        secondSelectData,
        scheduleType,
        scheduledDate,
        scheduledTime,
        isCancelTasksChecked
    ]);

    const handleChangeValue = (taskType: Task) => {
        setSelectedTask(taskType);

        const isSameSecondSelect =
            taskType.secondSelect === selectedTask?.secondSelect;

        !isSameSecondSelect && setSecondSelectData(null);
    };

    const handleSecondSelectData = (data: SecondSelectData | null) =>
        setSecondSelectData(data);

    const toggleTaskCancellation = () =>
        setIsCancelTasksChecked(!isCancelTasksChecked);

    const handleScheduleType = (type: SCHEDULE_TASK_TYPE | null) => {
        setScheduleType(type);
        setScheduledDate(null);
        setScheduledTime(null);
    };

    const handleScheduledDate = (data: ScheduledDatePayload) =>
        setScheduledDate(data);

    const handleScheduledTime = (data: ScheduledTimePayload) =>
        setScheduledTime(data);

    const isRestricted = isFeatureSettingsRestricted(
        FEATURE_SETTINGS.TaskScheduleSupport
    );

    return {
        arePlatformsLoading,
        isCancelTasksChecked,
        isRestricted,
        scheduleType,
        taskTypeInputValue,
        selectedTask,
        tasks,
        setTaskTypeInputValue,
        handleChangeValue,
        handleScheduledDate,
        handleScheduledTime,
        handleScheduleType,
        handleSecondSelectData,
        toggleTaskCancellation
    };
};

export { useCreateTaskContent };
